import * as XLSX from "xlsx";
import { IClub, IClubMember } from "../interface/interface";
import GAMEAPI from "../../../api/game";

// 날짜 포맷팅 함수
export function formatDate(dateString: string): string {
  if (!dateString) return "";
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const seconds = date.getSeconds().toString().padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

export async function exportMemberListToExcel(
  memberList: IClubMember[]
): Promise<void> {
  try {
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(memberList);
    XLSX.utils.book_append_sheet(workbook, worksheet, "멤버 목록");
    XLSX.writeFile(workbook, `멤버목록_${getCurrentDate()}.xlsx`);
  } catch (error) {
    console.error("멤버 목록 엑셀 내보내기 실패:", error);
    alert("엑셀 파일 생성 중 오류가 발생했습니다.");
  }
}

// 기본 클럽 정보 엑셀 내보내기
export async function exportClubListToExcel(
  setIsLoading: (loading: boolean) => void
): Promise<void> {
  try {
    setIsLoading(true);

    // 1. 전체 클럽 목록 가져오기
    const clubListResponse = await GAMEAPI.getClubList(1, 1000);
    let clubData: IClub[] = [];

    if (clubListResponse.data.result === 1 && clubListResponse.data.payload) {
      if (Array.isArray(clubListResponse.data.payload)) {
        clubData = clubListResponse.data.payload;
      } else if (clubListResponse.data.payload.clubs) {
        clubData = clubListResponse.data.payload.clubs;
      } else if (clubListResponse.data.payload.list) {
        clubData = clubListResponse.data.payload.list;
      } else {
        clubData = [clubListResponse.data.payload];
      }
    }

    console.log(`총 ${clubData.length}개 클럽의 상세 정보를 가져오는 중...`);

    // 2. 각 클럽의 상세 정보 가져오기
    const detailedClubData: IClub[] = [];

    for (let i = 0; i < clubData.length; i++) {
      const club = clubData[i];
      console.log(
        `${i + 1}/${clubData.length}: 클럽 ${club.name} 상세 정보 로딩 중...`
      );

      try {
        // 상세 정보 가져오기
        const detailResponse = await GAMEAPI.getMyClub(club.clubId);
        let detailedClub = club; // 기본값으로 기존 데이터 사용

        if (detailResponse.data.result === 1 && detailResponse.data.payload) {
          detailedClub = { ...club, ...detailResponse.data.payload };
        }

        detailedClubData.push(detailedClub);

        // API 호출 간격 조절 (너무 빠르게 호출하지 않도록)
        await new Promise((resolve) => setTimeout(resolve, 100));
      } catch (error) {
        console.error(`클럽 ${club.name} 상세 정보 로드 실패:`, error);
        // 오류가 발생해도 기본 데이터라도 포함
        detailedClubData.push(club);
      }
    }

    // 3. 엑셀 데이터 준비
    const excelData = detailedClubData.map((club, index) => ({
      순번: index + 1,
      클럽ID: club.clubId,
      클럽명: club.name,
      "클럽 설명": club.description,
      "소유자 ID": club.ownerUserId,
      "멤버 수": club.memberCount,
      "클럽 순위": club.clubRank,
      "총 핸드 수": club.totalHandCount,
      "총 링 상금": club.totalRingWinAmount,
      "총 링 승리 횟수": club.totalRingWinCount,
      "토너먼트 승리 횟수": club.totalTournamentWinCount,
      "토너먼트 참가 횟수": club.totalTournamentJoinCount,
      "총 베팅 금액": club.totalBetAmount,
      "총 레이크 금액": club.totalRakeAmount,
      "Top 총 핸드 수": club.topTotalHandCount,
      "Top 총 링 상금": club.topTotalRingWinAmount,
      "Top 총 링 승리 횟수": club.topTotalRingWinCount,
      "Top 토너먼트 참가 횟수": club.topTotalTournamentJoinCount,
      "Top 토너먼트 승리 횟수": club.topTotalTournamentWinCount,
      "Top 총 베팅 금액": club.topTotalBetAmount,
      "Top 총 레이크 금액": club.topTotalRakeAmount,
      "통계 업데이트": club.statsUpdatedAt
        ? formatDate(club.statsUpdatedAt)
        : "",
      "이미지 URL": club.imageUrl || "",
    }));

    // 4. 엑셀 파일 생성 및 다운로드
    await createAndDownloadExcel(
      excelData,
      "클럽 목록",
      `클럽목록_${getCurrentDate()}.xlsx`
    );

    alert(
      `클럽 데이터가 성공적으로 엑셀 파일로 저장되었습니다!\n총 ${detailedClubData.length}개 클럽 데이터`
    );
  } catch (error) {
    console.error("엑셀 내보내기 실패:", error);
    alert("엑셀 파일 생성 중 오류가 발생했습니다.");
  } finally {
    setIsLoading(false);
  }
}

// 멤버 정보도 포함한 상세 엑셀 내보내기
export async function exportDetailedClubDataToExcel(
  setIsLoading: (loading: boolean) => void
): Promise<void> {
  try {
    setIsLoading(true);

    // 클럽 목록과 상세 정보 가져오기
    const clubListResponse = await GAMEAPI.getClubList(1, 1000);
    let clubData: IClub[] = [];

    if (clubListResponse.data.result === 1 && clubListResponse.data.payload) {
      if (Array.isArray(clubListResponse.data.payload)) {
        clubData = clubListResponse.data.payload;
      } else if (clubListResponse.data.payload.clubs) {
        clubData = clubListResponse.data.payload.clubs;
      } else if (clubListResponse.data.payload.list) {
        clubData = clubListResponse.data.payload.list;
      } else {
        clubData = [clubListResponse.data.payload];
      }
    }

    const detailedClubData: IClub[] = [];
    const allMemberData: any[] = [];

    for (let i = 0; i < clubData.length; i++) {
      const club = clubData[i];
      console.log(`${i + 1}/${clubData.length}: 클럽 ${club.name} 처리 중...`);

      try {
        const detailResponse = await GAMEAPI.getMyClub(club.clubId);
        let detailedClub = club;

        if (detailResponse.data.result === 1 && detailResponse.data.payload) {
          detailedClub = { ...club, ...detailResponse.data.payload };
        }

        const memberResponse = await GAMEAPI.getMemberList(club.clubId);
        let memberData: IClubMember[] = [];

        if (
          memberResponse.data.result === 1 &&
          memberResponse.data.payload?.members
        ) {
          memberData = memberResponse.data.payload.members;

          // 멤버 데이터에 클럽 정보 추가
          memberData.forEach((member) => {
            allMemberData.push({
              클럽ID: club.clubId,
              클럽명: club.name,
              사용자ID: member.userId,
              닉네임: member.nickname,
              역할: member.role === "LEADER" ? "리더" : "멤버",
              "핸드 수": member.handCount,
              "승리 금액": member.winAmount,
              "베팅 금액": member.betAmount,
              "승리 횟수": member.winCount,
              "토너먼트 참가 횟수": member.tournamentJoinCount,
              "마지막 접속": member.lastAccessAt
                ? formatDate(member.lastAccessAt)
                : "",
              "플레이 중": member.isPlaying ? "Y" : "N",
              "프로필 이미지": member.profileImage || "",
            });
          });
        }

        detailedClubData.push(detailedClub);
        await new Promise((resolve) => setTimeout(resolve, 100));
      } catch (error) {
        console.error(`클럽 ${club.name} 처리 실패:`, error);
        detailedClubData.push(club);
      }
    }

    // 워크북 생성
    const workbook = XLSX.utils.book_new();

    // 1. 클럽 정보 시트
    const clubExcelData = detailedClubData.map((club, index) => ({
      순번: index + 1,
      클럽ID: club.clubId,
      클럽명: club.name,
      "클럽 설명": club.description,
      "소유자 ID": club.ownerUserId,
      "멤버 수": club.memberCount,
      "클럽 순위": club.clubRank,
      "총 핸드 수": club.totalHandCount,
      "총 링 상금": club.totalRingWinAmount,
      "총 링 승리 횟수": club.totalRingWinCount,
      "토너먼트 승리 횟수": club.totalTournamentWinCount,
      "토너먼트 참가 횟수": club.totalTournamentJoinCount,
      "총 베팅 금액": club.totalBetAmount,
      "총 레이크 금액": club.totalRakeAmount,
      "통계 업데이트": club.statsUpdatedAt
        ? formatDate(club.statsUpdatedAt)
        : "",
    }));

    const clubWorksheet = XLSX.utils.json_to_sheet(clubExcelData);
    XLSX.utils.book_append_sheet(workbook, clubWorksheet, "클럽 목록");

    // 2. 멤버 정보 시트
    if (allMemberData.length > 0) {
      const memberWorksheet = XLSX.utils.json_to_sheet(allMemberData);
      XLSX.utils.book_append_sheet(workbook, memberWorksheet, "멤버 목록");
    }

    // 파일 다운로드
    const fileName = `클럽_상세정보_${getCurrentDate()}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    alert(
      `상세 데이터가 성공적으로 저장되었습니다!\n파일명: ${fileName}\n클럽: ${detailedClubData.length}개\n멤버: ${allMemberData.length}명`
    );
  } catch (error) {
    console.error("상세 엑셀 내보내기 실패:", error);
    alert("엑셀 파일 생성 중 오류가 발생했습니다.");
  } finally {
    setIsLoading(false);
  }
}

// 엑셀 파일 생성 및 다운로드 헬퍼 함수
async function createAndDownloadExcel(
  data: any[],
  sheetName: string,
  fileName: string
): Promise<void> {
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();

  // 워크시트 이름 설정
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  // 컬럼 너비 자동 조절
  const columnWidths: any[] = [];
  const headers = Object.keys(data[0] || {});

  headers.forEach((header, index) => {
    const maxLength = Math.max(
      header.length,
      ...data.map((row) => String(row[header] || "").length)
    );
    columnWidths[index] = { width: Math.min(maxLength + 2, 50) };
  });

  worksheet["!cols"] = columnWidths;

  // 파일 다운로드
  XLSX.writeFile(workbook, fileName);
}

// 현재 날짜 문자열 반환
function getCurrentDate(): string {
  return new Date().toISOString().slice(0, 10);
}
